// @ts-strict-ignore
import {Component, inject, Inject, Injector, LOCALE_ID, OnInit} from "@angular/core";
import {
	BasicMaterialDialog,
	MatDialogDataBaseInput,
	MatDialogDataBaseOutput,
} from "../../basic-material-dialog/basic-material-dialog.directive";
import {
	SimConfigurationService,
	SimExportService,
	SimMaterialDialogService,
	SimRecorderService,
	SimRecordingsService,
	SimSessionService,
	SimSystemExtendedService,
	SimUserExtendedService,
	TrackByMethods,
} from "@services";
import {Router} from "@angular/router";
import {TranslateService} from "@ngx-translate/core";
import {
	Event,
	FontAwesomeCode,
	ISimSession,
	Modules,
	RecorderMapping,
	Scenario,
	ScenarioRecording,
	ScenarioRecordingGroup,
	ScenarioRecordingState,
	SessionState,
	TrainingSession,
} from "@models";
import Enumerable from "linq";
import {Observable} from "rxjs";
import {Translations} from "@translations";
import {EventEmitterService} from "@event-emitter";

export interface DialogDataProRecordingFinishedDialog extends MatDialogDataBaseInput {
	trainingSession: TrainingSession;
	event: Event;
	scenarioRecordingId: string;
	stopRecordingFinishedEmitter: Observable<any>;
	recorderMapping: RecorderMapping;
	scenarioRecordingIds: string[];
}

export interface DialogDataProRecordingFinishedDialogOutput extends MatDialogDataBaseOutput {
	followUpAction: "archive" | "dashboard" | "sessionSelection" | "continue";
}

@Component({
	selector: "sim-pro-recording-finished-dialog",
	templateUrl: "./pro-recording-finished-dialog.component.html",
	styleUrls: ["./pro-recording-finished-dialog.component.scss"],
	providers: [EventEmitterService],
	standalone: false,
})
export class ProRecordingFinishedDialogComponent
	extends BasicMaterialDialog<
		ProRecordingFinishedDialogComponent,
		DialogDataProRecordingFinishedDialog,
		DialogDataProRecordingFinishedDialogOutput
	>
	implements OnInit
{
	readonly translateService = inject(TranslateService);
	T = Translations;
	TrackByMethods = TrackByMethods;

	hasLicenseModule = SimConfigurationService.hasLicenseModule;
	Modules = Modules;

	public trainingSession: TrainingSession = null;
	public scenarioRecordings: ScenarioRecording[] = null;

	settings = SimConfigurationService.settings;

	errorMessage: string = null;
	stopRecordingFinished = false;

	disableResultExpansion = true;

	// Possible values
	// ===============
	// RecordingCopied
	// WaitForRecordingCopyFinished
	// ExportRunning
	// ExportFinished
	// StopRecordingFailed
	public state: "RecordingCopied" | "WaitForRecordingCopyFinished" | "ExportRunning" | "ExportFinished" | "StopRecordingFailed" =
		"WaitForRecordingCopyFinished";
	public isFinished = false;
	public allRecordingsStopped = false;

	public recordingFinishedDialogViewState: "NoScheduledRunsOrSessions" | "ScheduledRunsAvailable" | "ScheduledSessionsAvailable";
	public nextScenarioRecordingGroup: ScenarioRecordingGroup;
	nextSession: ISimSession;

	constructor(
		_injector: Injector,
		private _simExportService: SimExportService,
		private _emitterService: EventEmitterService,
		private _simSystemService: SimSystemExtendedService,
		private _simRecorderService: SimRecorderService,
		private _simRecordingsService: SimRecordingsService,
		private _simUserService: SimUserExtendedService,
		private _router: Router,
		private _simDialogService: SimMaterialDialogService,
		private translate: TranslateService,
		private _simSessionService: SimSessionService,
		private _simRecordingService: SimRecordingsService,
		@Inject(LOCALE_ID) public locale: string,
	) {
		super(_injector);

		//Evaluate Stopping Views depending on the state of the Event / Session currently stopped

		//1. Check if training Session has scheduled runs
		//If Training Session State is finished check if the event has scheduled Sessions
		if (this.data.trainingSession.state === SessionState.Finished) {
			//Check if current Training Session is last of event
			if (this.data.event.sessionIds[this.data.event.sessionIds.length - 1] === this.data.trainingSession.id) {
				this.recordingFinishedDialogViewState = "NoScheduledRunsOrSessions";
			} else {
				this.recordingFinishedDialogViewState = "ScheduledSessionsAvailable";
				let indexOfNextSession =
					this.data.event.sessionIds.findIndex((sessionId) => sessionId === this.data.trainingSession.id) + 1;
				this._simSessionService
					.getSessionById(this.data.event.sessionIds[indexOfNextSession])
					.then((nextSession) => (this.nextSession = nextSession));
			}
		} else {
			//Check if there are scheduled session runs
			this.data.trainingSession.scenarioRecordingGroups.forEach((scenarioRecordingGroup, indexScenarioRecordingGroup) => {
				if (scenarioRecordingGroup.scenarioRecordingsMap[0].scenarioRecordingId === this.data.scenarioRecordingIds[0]) {
					//Check if it is the last scenarioRecordingRun
					if (indexScenarioRecordingGroup === this.data.trainingSession.scenarioRecordingGroups.length - 1) {
						//It is the last run
						//Check if current Training Session is last of event
						if (this.data.event.sessionIds[this.data.event.sessionIds.length - 1] === this.data.trainingSession.id) {
							this.recordingFinishedDialogViewState = "NoScheduledRunsOrSessions";
						} else {
							this.recordingFinishedDialogViewState = "ScheduledSessionsAvailable";

							let indexOfNextSession =
								this.data.event.sessionIds.findIndex((sessionId) => sessionId === this.data.trainingSession.id) + 1;
							this._simSessionService
								.getSessionById(this.data.event.sessionIds[indexOfNextSession])
								.then((nextSession) => (this.nextSession = nextSession));
						}
					} else {
						this.recordingFinishedDialogViewState = "ScheduledRunsAvailable";
						this.nextScenarioRecordingGroup =
							this.data.trainingSession.scenarioRecordingGroups[indexScenarioRecordingGroup + 1];
					}
				}
			});
		}

		console.log(this.data);
		console.log(this.data.event);
	}

	public ngOnInit() {
		super.ngOnInit();

		this.initializeTrainingSession();

		this.disableResultExpansion = !this._simUserService.user.isHost;

		this.data.stopRecordingFinishedEmitter?.subscribe(() => {
			this.stopRecordingFinished = true;
		});
	}

	public skipCopyProgress() {
		this.state = "RecordingCopied";
	}

	public getCloseButtonText() {
		switch (this.state) {
			case "WaitForRecordingCopyFinished":
				return "Export later";
			case "RecordingCopied":
				return "Export later";
			case "ExportRunning":
				return "Close";
			case "ExportFinished":
				return "Close";
			case "StopRecordingFailed":
				return "Close";
		}
	}

	public continueTrainingSession() {
		if (this.trainingSession.state === SessionState.Finished) {
			this._router.navigate(["/session-selection/pro-session-selection"], {
				queryParams: {
					selectedEventId: this.data.event.id,
					selectedSessionId: this.data.trainingSession.id,
				},
				queryParamsHandling: "merge", // remove to replace all query params by provided
			});
		} else {
			this._simRecorderService
				.continueTrainingSession({
					trainingSessionId: this.trainingSession.id,
					recorderMappingId: this.data.recorderMapping.id,
					scenarioId: null,
				})
				.then((result) => {
					this.close({action: "ok", followUpAction: "continue"});
				});
		}
	}

	public navigateToArchive() {
		this.close({action: "ok", followUpAction: "archive"});
	}

	public navigateToDashboard() {
		this.close({action: "ok", followUpAction: "dashboard"});
	}

	public navigateToSessionSelection() {
		this.close({action: "ok", followUpAction: "sessionSelection"});
	}

	public logout() {
		this._simUserService.logout();
	}

	createNewTrainingSession() {
		if (SimConfigurationService.hasLicenseModule(Modules.EventDesigner)) {
			this._router.navigate(["/session-selection/pro-session-selection"], {
				queryParams: {
					selectedEventId: this.data.event.id,
				},
				queryParamsHandling: "merge", // remove to replace all query params by provided
			});
		} else {
			this._router.navigate(["/session-selection/pro-session-selection"]);
		}
	}

	getDialogTitle() {
		switch (this.recordingFinishedDialogViewState) {
			case "ScheduledRunsAvailable":
				return this.translate.instant(this.T.PRO_RECORDING_DIALOG_TITLE_MULTIPLE, {
					sessionName: this.nextScenarioRecordingGroup.name,
					totalSessions: this.trainingSession.scenarioRecordingGroups.length,
				});
			case "NoScheduledRunsOrSessions":
				return this.translate.instant(this.T.PRO_RECORDING_DIALOG_TITLE_FINISHED);
			case "ScheduledSessionsAvailable":
				return this.translate.instant(this.T.PRO_RECORDING_DIALOG_TITLE, {
					sessionName: this.trainingSession.title,
				});
			default:
				return this.translate.instant(this.T.PRO_RECORDING_DIALOG_TITLE_FINISHED);
		}
	}

	public openSessionInEventDashboard() {
		this._router.navigate(["/event-detail/", this.data.event.id]);
	}

	openScenarioInfoCon(nextScenario: Scenario) {
		this._simDialogService.openScenarioInformationDialog({
			scenario: nextScenario,
		});
	}

	openChangeScenarioForNextRunDialog(scenario: Scenario) {
		this._simDialogService.openChangeScenarioDialog({scenario: scenario}).then((result) => {
			if (result.action === "ok") {
				if (result.newScenario) {
					console.log(result.newScenario);
					this.nextScenarioRecordingGroup.scenarioRecordingsMap[0].scenarioId = result.newScenario.id;

					this._simRecorderService.updateRecordingMetaData({
						scenarioRecordingId: this.nextScenarioRecordingGroup.scenarioRecordingsMap[0].scenarioRecordingId,
						scenarioId: result.newScenario.id,
					});
				}
			}
		});
	}

	openInArchive() {
		this._router.navigate([`/web-manager/list/scenario-archive/player/0/${this.data.scenarioRecordingId}`]);
	}

	getHeaderIcon(): FontAwesomeCode {
		let result: FontAwesomeCode;
		switch (this.recordingFinishedDialogViewState) {
			case "ScheduledRunsAvailable":
				result = {fontIcon: "fa-check", fontSet: "fad"};
				break;
			case "ScheduledSessionsAvailable":
				result = {fontIcon: "fa-flag", fontSet: "fad"};
				break;
			case "NoScheduledRunsOrSessions":
				result = {fontIcon: "fa-check", fontSet: "fad"};
				break;
		}
		return result;
	}

	protected initializeTrainingSession() {
		this.trainingSession = this.data.trainingSession;

		this.initializeScenarioRecordings();
		this.initializeEventhandlers();
	}

	private initializeEventhandlers() {
		this._emitterService.onScenarioRecordingChanged.subscribe(async (args) => {
			const foundScenarioRecording = Enumerable.from(this.scenarioRecordings).firstOrDefault(
				(scenarioRecording) => scenarioRecording.id === args?.scenarioRecordingId,
			);

			if (foundScenarioRecording) {
				let rec = await this._simRecordingService.getRecordingById(args.scenarioRecordingId);
				Object.assign(foundScenarioRecording, rec);
				this.updateDownloadProgress(foundScenarioRecording, null);

				this.allRecordingsStopped = this.scenarioRecordings.every((x) => x.state !== ScenarioRecordingState.Recording);

				if (Enumerable.from(this.scenarioRecordings).all((scenarioRecording) => scenarioRecording.isReadyForArchive)) {
					this.state = "RecordingCopied";
					this.isFinished = true;
				}
			}
		});

		this._emitterService.onScenarioRecordingDownloadProgressChanged.subscribe((args) => {
			const foundScenarioRecording = Enumerable.from(this.scenarioRecordings).firstOrDefault(
				(scenarioRecording) => scenarioRecording.id === args.scenarioRecordingId,
			);

			if (foundScenarioRecording) {
				this.updateDownloadProgress(foundScenarioRecording, args.progress);
			}
		});

		this._emitterService.onTrainingSessionUpdated.subscribe((updatedTrainingSession) => {
			if (this.trainingSession.id === updatedTrainingSession.simSession.id) {
				this.trainingSession = updatedTrainingSession.simSession as TrainingSession;
			}
		});
	}

	private updateDownloadProgress(recording: ScenarioRecording, progress: number) {
		if (recording) {
			progress ??= 0;
			if (recording.isReadyForArchive) {
				progress = 100;
			}
			recording["DownloadProgress"] = Math.max(progress, recording["DownloadProgress"] ?? 0);
		}
	}

	private initializeScenarioRecordings() {
		this._simRecordingsService.getScenarioRecordingsByIds(this.data.scenarioRecordingIds).then((result) => {
			this.scenarioRecordings = result;

			for (const recording of this.scenarioRecordings) {
				this.updateDownloadProgress(recording, null);
			}

			this.stopRecordingFinished =
				this.stopRecordingFinished || result.filter((x) => x.state != ScenarioRecordingState.Stopped).length == 0;
		});
	}
}
